import React, { Suspense } from "react";
import { createBrowserRouter, createRoutesFromElements, Navigate, Route, RouterProvider } from "react-router-dom";
import { QueryClientProvider, QueryClient } from "@tanstack/react-query";
import { RecoilRoot } from "recoil";
import Pages from "./definitions/Pages";
import RootLayout from "./components/UI/Layout/RootLayout";

const ScrollToTop = React.lazy(() => import("./components/UI/Layout/ScrollToTop"));
const Gestao = React.lazy(() => import("./pages/Gestao"));
const Analise = React.lazy(() => import("./pages/Analise"));
const SkuView = React.lazy(() => import("./components/Analise/SkuView"));
const Configuracao = React.lazy(() => import("./pages/Configuracao"));
const Triagem = React.lazy(() => import("./pages/Triagem"));
const HistoricalOptimization = React.lazy(() => import("./pages/HistoricalOptimization"));
const TriangularOptimization = React.lazy(() => import("./pages/TriangularOptimization"));
const Home = React.lazy(() => import("./pages/Home"));
const Reports = React.lazy(() => import("./pages/Reports"));
const Uploads = React.lazy(() => import("./pages/Uploads"));

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      /* Keep data warm for a minute so route changes and quick back-navigation don't immediately
       * refetch. Individual queries can still override when they need infinite stale time. */
      staleTime: 60_000,
      cacheTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      /* Default retry was 3 × 60s backoff, so a broken endpoint blocked the UI for ~3 minutes.
       * One retry surfaces real failures quickly while still absorbing transient flakes. */
      retry: 1,
    },
  },
});

const Fallback = () => {
  return (
    <></>
  );
};

const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route path="/" element={<RootLayout />} errorElement={<div>Ocorreu um erro inesperado</div>}>
        <Route
          index
          element={<Navigate to={`/${Pages.Home.path}`} replace />}
        />
        <Route
          path={`/${Pages.Home.path}`}
          element={
            <Suspense fallback={<Fallback />}>
              <Home />
            </Suspense>
          }
        />
        <Route
          path={`/${Pages.Gestao.path}`}
          element={
            <Suspense fallback={<Fallback />}>
              <Gestao />
            </Suspense>
          }
        />
        <Route
          path={`/${Pages.Triagem.path}`}
          element={
            <Suspense fallback={<Fallback />}>
              <Triagem />
            </Suspense>
          }
        />
        <Route
          path={`/${Pages.HistoricalOptimization.path}`}
          element={
            <Suspense fallback={<Fallback />}>
              <HistoricalOptimization />
            </Suspense>
          }
        />
        <Route
          path={`/${Pages.Uploads.path}`}
          element={
            <Suspense fallback={<Fallback />}>
              <Uploads />
            </Suspense>
          }
        />
        <Route
          path={`/${Pages.Reports.path}`}
          element={
            <Suspense fallback={<Fallback />}>
              <Reports />
            </Suspense>
          }
        />
        <Route
          path={`/${Pages.TriangularOptimization.path}`}
          element={
            <Suspense fallback={<Fallback />}>
              <TriangularOptimization />
            </Suspense>
          }
        />
        <Route
          path={`/${Pages.Configuracao.path}`}
          element={
            <Suspense fallback={<Fallback />}>
              <Configuracao />
            </Suspense>
          }
        />
        <Route
          path={`/${Pages.Analise.path}`}
          element={
            <Suspense fallback={<Fallback />}>
              <Analise />
            </Suspense>
          }
        >
          <Route
            index
            element={
              <Suspense fallback={<Fallback />}>
                <SkuView />
              </Suspense>
            }
          />
          <Route
            path=":idSku"
            element={
              <Suspense fallback={<Fallback />}>
                <SkuView />
              </Suspense>
            }
          />
        </Route>
      </Route>
    </>
  )
);


const App: React.FC = () => {
  return (
    <RecoilRoot>
      <ScrollToTop />
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        {/* <ReactQueryDevtools initialIsOpen={false} /> */}
      </QueryClientProvider>
    </RecoilRoot>
  );
};





export default App;
